/**
 * Sites Hook
 * 
 * Custom hook for managing sites data loading, caching, and error states.
 * Follows the same pattern as other data hooks in the application.
 */

import { useState, useEffect, useCallback } from 'react';
import { sitesService } from '../services/sites/sites-service';

interface UseSitesReturn {
  data: string[] | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Hook for fetching and managing sites data
 * @returns Object containing sites data, loading state, error state, and retry function
 */
export const useSites = (): UseSitesReturn => {
  const [data, setData] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSites = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const sites = await sitesService.getSites();
      setData(sites);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load sites';
      setError(errorMessage);
      console.error('Error fetching sites:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retry = useCallback(() => {
    fetchSites();
  }, [fetchSites]);

  // Load sites on component mount
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  return {
    data,
    isLoading,
    error,
    retry,
  };
};
